import { useEffect, useState } from 'react'
import { useMediaQuery } from 'react-responsive'

import styles from './title.module.scss'
import { VehicleConditionBadge } from '@/components/atomic/VehicleConditionBadge'
import { Badge } from '@/components/styleguide/Badge'
import { Breadcrumb } from '@/components/styleguide/Configurator/Breadcrumb'
import { TextSwitchTransition } from '@/lib/animations/TextSwitchTransition'
import { FILTER_LEASE_TYPES_PERSONAL, FILTER_VEHICLE_TYPES_VANS } from '@/lib/constants'
import { useExperiment } from '@/lib/hooks/useExperiment'
import { addVatToPrice } from '@/lib/utilities/search'
import { isProd } from '@/lib/utilities/system'
import { getUrlReadyString } from '@/lib/utilities/vehicle'
import { PageType } from '@/types/system'
import { Vehicle, VehiclePrice } from '@/types/vehicle'

export type TitleProps = {
  vehicle: Vehicle
  isSticky: boolean
  selectedVehiclePrice: VehiclePrice
  pageType?: PageType
}

export const Title = ({
  vehicle,
  isSticky,
  selectedVehiclePrice,
  pageType = 'config',
}: TitleProps) => {
  const isDesktop = useMediaQuery({ minWidth: 992 })
  const [monthlyPrice, setMonthlyPrice] = useState<string | number>('0.00')
  const [includeVat, setIncludeVat] = useState<boolean>(true)
  const [experimentGroup] = useExperiment()
  const [version, setVersion] = useState<'control' | 'variant-1' | 'variant-2'>('control')

  useEffect(() => {
    if (experimentGroup.startsWith('edit-lease-placement-variant')) {
      setVersion(experimentGroup.split('edit-lease-placement-')[1] as 'variant-1' | 'variant-2')
    }
  }, [experimentGroup])

  useEffect(() => {
    if (selectedVehiclePrice) {
      setMonthlyPrice(
        selectedVehiclePrice.leaseType === FILTER_LEASE_TYPES_PERSONAL
          ? addVatToPrice(selectedVehiclePrice.monthlyPayment)
          : selectedVehiclePrice.monthlyPayment.toFixed(2),
      )
    }
  }, [selectedVehiclePrice])

  useEffect(() => {
    if (selectedVehiclePrice) {
      setIncludeVat(selectedVehiclePrice.leaseType === FILTER_LEASE_TYPES_PERSONAL)
    }
  }, [selectedVehiclePrice])

  const breadcrumbs = [
    {
      label: `${vehicle.manufacturerName}`,
      link: `/${
        vehicle.type === FILTER_VEHICLE_TYPES_VANS ? 'van' : 'car'
      }-leasing/${getUrlReadyString(vehicle.manufacturerName)}`,
    },
    {
      label: `${vehicle.rangeName}`,
      link: `/${
        vehicle.type === FILTER_VEHICLE_TYPES_VANS ? 'van' : 'car'
      }-leasing/${getUrlReadyString(vehicle.manufacturerName)}/${getUrlReadyString(
        vehicle.rangeName,
      )}`,
    },
  ]

  return (
    <div
      className={`${styles.container} ${isSticky ? styles['is-sticky'] : ''} ${pageType === 'landing' ? styles['ad-title'] : ''}`}>
      <div className={styles.titles}>
        {pageType === 'config' &&
          (selectedVehiclePrice ? (
            <>
              <Badge
                className={styles['stock-status-badge']}
                type="stock-status"
                label={selectedVehiclePrice.stockStatus === 0 ? 'Factory order' : 'In stock'}
              />
              {vehicle.fuelType === 5 && (
                <Badge
                  label="Electric"
                  type="electric"
                />
              )}
            </>
          ) : (
            <VehicleConditionBadge
              isElectric={vehicle.fuelType === 5}
              size="sm"
            />
          ))}
        {isDesktop ? (
          <>
            <h1 className={!isProd() ? 'vehicle-name__h1' : ''}>
              {pageType !== 'config' && vehicle.manufacturerName} {vehicle.modelName}{' '}
              <TextSwitchTransition text={vehicle.derivativeName} />
            </h1>
            {version === 'control' && (
              <h3 className={styles.subtitle}>
                <span className={styles.large}>
                  £<TextSwitchTransition text={monthlyPrice.toString().slice(0, -2)} />
                </span>
                <TextSwitchTransition text={monthlyPrice.toString().slice(-2)} />
                /pm <TextSwitchTransition text={includeVat ? `incl.` : 'excl.'} /> VAT
              </h3>
            )}
          </>
        ) : (
          <>
            <h1 className={!isProd() ? 'vehicle-name__h1' : ''}>
              {pageType !== 'config' && vehicle.manufacturerName} {vehicle.modelName}
            </h1>
            <h2>{vehicle.derivativeName}</h2>
          </>
        )}
      </div>
      {pageType === 'config' && <Breadcrumb breadcrumbs={breadcrumbs} />}
    </div>
  )
}
